import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

const INACTIVITY_TIMEOUT = 60 * 60 * 1000; // 1 hour in milliseconds

const useInactivityTracker = () => {
  const [isInactive, setIsInactive] = useState(false);
  const [lastActivity, setLastActivity] = useState(Date.now());
  const location = useLocation();

  // Reset activity timer
  const resetActivityTimer = useCallback(() => {
    setLastActivity(Date.now());
    setIsInactive(false);
  }, []);

  // Check if current route is landing page
  const isLandingPage = location.pathname === '/' || location.pathname === '/home';

  // Activity event handlers
  const handleActivity = useCallback(() => {
    resetActivityTimer();
  }, [resetActivityTimer]);

  useEffect(() => {
    // Activity events to track
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click'
    ];

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    // Set up inactivity timer
    const inactivityTimer = setInterval(() => {
      const timeSinceLastActivity = Date.now() - lastActivity;
      
      if (timeSinceLastActivity >= INACTIVITY_TIMEOUT && isLandingPage) {
        setIsInactive(true);
      }
    }, 1000); // Check every second

    // Cleanup
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
      clearInterval(inactivityTimer);
    };
  }, [handleActivity, lastActivity, isLandingPage]);

  // Reset inactivity when route changes away from landing page
  useEffect(() => {
    if (!isLandingPage) {
      setIsInactive(false);
    }
  }, [isLandingPage]);

  return {
    isInactive,
    resetActivityTimer,
    isLandingPage
  };
};

export default useInactivityTracker;